import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import {
  ArrowRight,
  Building2,
  CheckCircle,
  GraduationCap,
  Handshake,
  Heart,
  PackageSearch,
  Shield,
  ShoppingCart,
  Sparkles,
  Truck,
  Users,
  WalletCards
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const CENTRAL_URL = 'https://centrale.objectifscopoutremer.com';
const PASS_URL = CENTRAL_URL + '/pass';

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.08 });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: isVisible ? delay + 'ms' : '0ms' }}
      className={
        'transition-transform duration-700 ' +
        (isVisible ? 'translate-y-0' : 'translate-y-2') +
        ' ' + className
      }
    >
      {children}
    </div>
  );
};

const offerCards = [
  {
    icon: Building2,
    eyebrow: 'Entreprises',
    title: 'Acheter, vendre, recruter et transporter',
    description: 'Un point d’entrée unique vers les achats mutualisés, la mise en marché, les compétences partagées et la logistique.',
    cta: 'Voir les services entreprises',
    to: '/services-entreprises',
    color: 'from-blue-600 to-indigo-600'
  },
  {
    icon: ShoppingCart,
    eyebrow: 'Particuliers',
    title: 'Vos achats du quotidien avec LOLODRIVE',
    description: 'Découvrez les produits, les relais de proximité et les conditions du PASS LOLODRIVE pour votre territoire.',
    cta: 'Découvrir PASS LOLODRIVE',
    href: PASS_URL,
    color: 'from-emerald-600 to-teal-600'
  },
  {
    icon: Handshake,
    eyebrow: 'Partenaires',
    title: 'Construire un partenariat utile',
    description: 'Présentez votre organisation et votre projet de coopération, de soutien ou de mise en relation.',
    cta: 'Échanger sur un partenariat',
    to: '/contact',
    color: 'from-purple-600 to-fuchsia-600'
  }
];

const solutions = [
  {
    icon: PackageSearch,
    title: 'Communityplace',
    description: 'La place de marché pour les achats mutualisés et les échanges professionnels.',
    href: CENTRAL_URL
  },
  {
    icon: Truck,
    title: 'LOGI’SCOP',
    description: 'Organisation du transport et de la logistique dans les Outre-mer.',
    to: '/logistique-mobilite'
  },
  {
    icon: Users,
    title: 'O’SCOP GE',
    description: 'Compétences partagées et solutions de recrutement pour les entreprises.',
    to: '/oscop-ge'
  },
  {
    icon: GraduationCap,
    title: 'FORMA’SCOP',
    description: 'Formation professionnelle adaptée aux besoins des territoires.',
    to: '/formascop'
  },
  {
    icon: WalletCards,
    title: 'F.O.G.E.D.O.M',
    description: 'Information et accompagnement des projets dans le cadre de l’entraide solidaire.',
    to: '/fogedom'
  },
  {
    icon: Shield,
    title: 'BTP & expertise',
    description: 'Garanties, expertise et dispositifs dédiés aux professionnels du bâtiment.',
    to: '/garanties-solidaires-btp'
  }
];

const ActionLink = ({ item, children, className = '' }) => {
  if (item.href) {
    return (
      <a href={item.href} className={className} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }
  return <Link to={item.to} className={className}>{children}</Link>;
};

const accessQuestions = [
  {
    question: 'À quoi sert le compte O’SCOP ?',
    answer: 'Le compte numérique permet de vous identifier et d’accéder à votre espace. Les démarches et conditions d’accès restent propres à chaque service.',
    to: '/register',
    label: 'Créer mon compte'
  },
  {
    question: 'Quelle est la différence avec l’adhésion FTPE ?',
    answer: 'La FTPE Outremer représente et accompagne les très petites entreprises. Son adhésion est une démarche distincte de la création du compte. La cotisation affichée est de 200 € par an ; les conditions d’accès aux dispositifs sont précisées sur leurs pages.',
    to: '/ftpe-outremer/pourquoi-adherer',
    label: 'Comprendre l’adhésion FTPE'
  },
  {
    question: 'Créer un compte rend-il associé de la coopérative ?',
    answer: 'L’admission comme associé coopérateur fait l’objet d’une démarche distincte, selon les statuts de la coopérative. Elle ne résulte pas de la seule création d’un compte numérique.',
    to: '/qui-sommes-nous',
    label: 'Découvrir la coopérative'
  },
  {
    question: 'Comment connaître les conditions d’un service ?',
    answer: 'Consultez la page du service pour identifier le public concerné, les modalités d’accès et le bon interlocuteur. Contactez l’équipe pour préciser votre besoin avant de vous engager.',
    to: '/services',
    label: 'Consulter les services'
  }
];

const OscopHomePage = () => {
  return (
    <div className="min-h-screen overflow-x-clip bg-slate-950 text-white">
      {/* En flux sur mobile : le bandeau réserve sa hauteur réelle, même lorsqu’il se replie ou se ferme. */}
      <Navigation mobileInFlow />

      <main>
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950" aria-hidden="true" />
          <div className="absolute -left-32 top-20 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl" aria-hidden="true" />
          <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-fuchsia-500/15 blur-3xl" aria-hidden="true" />

          <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-12 sm:px-6 lg:px-8 lg:pb-28 lg:pt-36">
            <AnimatedSection>
              <div className="mx-auto max-w-5xl text-center">
                <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-sm font-medium text-emerald-200">
                  <Shield className="h-4 w-4" aria-hidden="true" />
                  Société coopérative d’intérêt collectif
                </div>

                <h1 className="text-4xl font-black tracking-tight sm:text-6xl lg:text-7xl">
                  O’SCOP, la force du collectif
                  <span className="block bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">
                    dans les Outre-mer.
                  </span>
                </h1>

                <p className="mx-auto mt-7 max-w-3xl text-lg leading-relaxed text-slate-300 sm:text-xl">
                  OBJECTIF SCOP OUTREMER réunit entreprises, particuliers et partenaires autour de services
                  coopératifs : achats mutualisés, logistique, emploi partagé, formation et accompagnement
                  de projets solidaires.
                </p>

                <div className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:flex-wrap">
                  <Link to="/services" className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-7 py-4 text-base font-semibold text-white transition hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo-300">
                    Découvrir nos services
                    <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                  </Link>
                  <Link to="/register" className="inline-flex items-center justify-center rounded-md border border-white/20 bg-white/5 px-7 py-4 text-base font-semibold text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo-300">
                    Créer mon espace
                  </Link>
                </div>

                <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-sm text-slate-300">
                  {['Coopératif', 'Multisectoriel', 'Ancré dans les territoires'].map((label) => (
                    <span key={label} className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2">
                      <CheckCircle className="h-4 w-4 text-emerald-400" aria-hidden="true" />
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        <section className="bg-slate-950 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="mx-auto mb-12 max-w-3xl text-center">
                <span className="text-sm font-semibold uppercase tracking-widest text-indigo-300">Choisissez votre parcours</span>
                <h2 className="mt-3 text-3xl font-bold sm:text-5xl">Une porte d’entrée pour chaque besoin</h2>
                <p className="mt-4 text-lg text-slate-400">Accédez directement aux services qui correspondent à votre situation.</p>
              </div>
            </AnimatedSection>

            <div className="grid gap-6 lg:grid-cols-3">
              {offerCards.map((item, index) => (
                <AnimatedSection key={item.title} delay={index * 90}>
                  <Card className="group h-full overflow-hidden rounded-3xl border-slate-800 bg-slate-900/70 transition hover:-translate-y-1 hover:border-slate-600">
                    <div className={'h-1.5 bg-gradient-to-r ' + item.color} />
                    <CardContent className="flex h-full flex-col p-7">
                      <div className={'mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ' + item.color}>
                        <item.icon className="h-7 w-7 text-white" aria-hidden="true" />
                      </div>
                      <p className="text-sm font-semibold uppercase tracking-wider text-indigo-300">{item.eyebrow}</p>
                      <h3 className="mt-2 text-2xl font-bold text-white">{item.title}</h3>
                      <p className="mt-4 flex-1 leading-relaxed text-slate-400">{item.description}</p>
                      <ActionLink item={item} className="mt-7 inline-flex items-center font-semibold text-indigo-300 hover:text-indigo-200">
                        {item.cta}
                        <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                      </ActionLink>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-white/5 bg-slate-900 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="mb-12 max-w-3xl">
                <span className="text-sm font-semibold uppercase tracking-widest text-emerald-300">Des solutions concrètes</span>
                <h2 className="mt-3 text-3xl font-bold sm:text-5xl">Activez les bons leviers au bon moment</h2>
                <p className="mt-4 text-lg text-slate-400">
                  Chaque dispositif répond à un besoin opérationnel et vous oriente vers une démarche claire.
                </p>
              </div>
            </AnimatedSection>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {solutions.map((solution, index) => (
                <AnimatedSection key={solution.title} delay={index * 70}>
                  <ActionLink item={solution} className="group block h-full">
                    <div className="h-full rounded-2xl border border-slate-800 bg-slate-950/60 p-6 transition hover:border-indigo-400/50 hover:bg-slate-950">
                      <solution.icon className="h-8 w-8 text-indigo-300" aria-hidden="true" />
                      <h3 className="mt-5 text-xl font-bold">{solution.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-400">{solution.description}</p>
                      <span className="mt-5 inline-flex items-center text-sm font-semibold text-indigo-300">
                        Découvrir
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                      </span>
                    </div>
                  </ActionLink>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-20 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <AnimatedSection>
              <div className="h-full rounded-3xl border border-indigo-400/20 bg-gradient-to-br from-indigo-950/80 to-slate-900 p-8 sm:p-10">
                <Shield className="h-10 w-10 text-indigo-300" aria-hidden="true" />
                <h2 className="mt-6 text-3xl font-bold">Un cadre coopératif lisible</h2>
                <p className="mt-4 leading-relaxed text-slate-300">
                  L’adhésion à la FTPE Outremer ouvre le cadre institutionnel d’accès aux dispositifs solidaires.
                  Chaque service conserve ses conditions propres, présentées avant tout engagement.
                </p>
                <Link to="/ftpe-outremer/pourquoi-adherer" className="mt-7 inline-flex items-center font-semibold text-indigo-300 hover:text-indigo-200">
                  Comprendre l’adhésion FTPE
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <div className="h-full rounded-3xl border border-purple-400/20 bg-gradient-to-br from-purple-950/70 to-slate-900 p-8 sm:p-10">
                <Heart className="h-10 w-10 text-pink-300" aria-hidden="true" />
                <h2 className="mt-6 text-3xl font-bold">Votre organisation peut faire la différence</h2>
                <p className="mt-4 leading-relaxed text-slate-300">
                  Entreprises, fondations et institutions : échangeons sur les actions à soutenir,
                  les contributions possibles et leur suivi.
                </p>
                <div className="mt-7 flex flex-wrap gap-4">
                  <Link to="/devenir-sponsor" className="inline-flex items-center font-semibold text-pink-300 hover:text-pink-200">
                    Proposer un soutien
                    <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                  </Link>
                  <Link to="/projets-a-soutenir" className="inline-flex items-center font-semibold text-purple-300 hover:text-purple-200">
                    Voir les projets
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        <section className="border-t border-white/10 bg-slate-900 py-16 sm:py-20" aria-labelledby="access-questions-title">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <p className="text-sm font-semibold uppercase tracking-widest text-emerald-300">Bien démarrer</p>
            <h2 id="access-questions-title" className="mt-3 text-3xl font-bold sm:text-4xl">Compte, adhésion et services : vos questions</h2>
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              {accessQuestions.map((item) => (
                <article key={item.question} className="rounded-2xl border border-slate-700 bg-slate-950/50 p-6">
                  <h3 className="text-lg font-semibold">{item.question}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">{item.answer}</p>
                  <Link to={item.to} className="mt-4 inline-flex items-center text-sm font-semibold text-indigo-300 hover:text-indigo-200">
                    {item.label}<ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden border-t border-white/10 py-20 sm:py-24">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-950 via-purple-950 to-slate-950" aria-hidden="true" />
          <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <Sparkles className="mx-auto h-9 w-9 text-yellow-300" aria-hidden="true" />
            <h2 className="mt-5 text-3xl font-bold sm:text-5xl">Parlons de votre besoin</h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
              Créez votre espace pour accéder à l’écosystème, ou contactez-nous pour être orienté vers la bonne solution.
            </p>
            <div className="mt-9 flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/register">
                <Button size="lg" className="w-full bg-indigo-600 px-9 py-6 text-base font-semibold hover:bg-indigo-500 sm:w-auto">
                  Créer mon espace
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="w-full border-white/20 bg-white/5 px-9 py-6 text-base text-white hover:bg-white/10 sm:w-auto">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OscopHomePage;
